import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { ArrowRight, Check, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const CONSENT_TEXT =
  "I agree that OnTenacity may email my result and contact me about a suitable next step using the details I provide.";

const QuizContactModal = ({ open, onOpenChange, answers, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    title: "",
    email: "",
    whatsapp: "",
    website: "",
    consent: false,
    honeypot: "", // spam trap
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    // Clear specific error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleConsentChange = (checked) => {
    setFormData((prev) => ({ ...prev, consent: !!checked }));
    if (errors.consent) setErrors((prev) => ({ ...prev, consent: null }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Name is required";
    if (!formData.company.trim()) e.company = "Company is required";
    if (!formData.title.trim()) e.title = "Title / role is required";
    if (!formData.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Please enter a valid email";
    if (!formData.whatsapp.trim()) e.whatsapp = "WhatsApp number is required";
    else if (!/^\+?[\d\s\-\(\)]{5,40}$/.test(formData.whatsapp)) e.whatsapp = "Please enter a valid phone number";
    if (formData.website.trim()) {
      const w = formData.website.trim();
      const normalized = w.startsWith("http") ? w : `https://${w}`;
      try {
        const u = new URL(normalized);
        if (!/^https?:$/.test(u.protocol)) e.website = "Website must start with http:// or https://";
      } catch {
        e.website = "Please enter a valid website URL";
      }
    }
    if (!formData.consent) e.consent = "You must agree to proceed";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (!answers || answers.length !== 5) {
      toast.error("Please complete all 5 quiz questions before submitting");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        company: formData.company.trim(),
        title: formData.title.trim(),
        email: formData.email.trim(),
        whatsapp: formData.whatsapp.trim(),
        website: formData.website.trim() || null,
        consent: formData.consent,
        answers,
        source_page: "homepage",
        honeypot: formData.honeypot || null,
      };

      const resp = await fetch(`${API}/api/quiz/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: "Submission failed" }));
        throw new Error(err.detail || "Submission failed");
      }

      const data = await resp.json();
      setSubmitted(true);
      if (onSuccess) onSuccess(data);
      toast.success("Your profile is on its way!");
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setSubmitted(false);
    setErrors({});
    setFormData({
      name: "",
      company: "",
      title: "",
      email: "",
      whatsapp: "",
      website: "",
      consent: false,
      honeypot: "",
    });
    onOpenChange(false);
  };

  const firstName = formData.name.trim().split(" ")[0] || "there";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-lg bg-[#0F0F10] border border-white/10 text-white max-h-[90vh] overflow-y-auto"
        data-testid="quiz-contact-modal"
        data-lenis-prevent
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-['Outfit'] text-white">
            {submitted ? "Thank you" : "Start your AI journey"}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {submitted
              ? `Your profile is on its way to ${formData.email}. Your details have also been shared with OnTenacity.`
              : "Receive your starting profile by email and share your details with OnTenacity to discuss a suitable next step."}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4 py-4" noValidate>
            {/* Honeypot (hidden) */}
            <input
              type="text"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Priya Sharma"
                error={errors.name}
                testId="contact-name-input"
              />
              <FormField
                label="Company *"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Acme Corp"
                error={errors.company}
                testId="contact-company-input"
              />
            </div>

            <FormField
              label="Title / role *"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="CEO, CIO, Director, etc."
              error={errors.title}
              testId="contact-title-input"
            />

            <FormField
              label="Email address *"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="priya@company.com"
              error={errors.email}
              testId="contact-email-input"
            />

            <FormField
              label="WhatsApp number *"
              name="whatsapp"
              type="tel"
              value={formData.whatsapp}
              onChange={handleChange}
              placeholder="+91 98765 43210"
              error={errors.whatsapp}
              testId="contact-whatsapp-input"
              hint="Include country code. We won't message this number automatically."
            />

            <FormField
              label="Website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="company.com (optional)"
              error={errors.website}
              testId="contact-website-input"
            />

            {/* Consent */}
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.consent}
                  onCheckedChange={handleConsentChange}
                  className="mt-0.5 border-white/20 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-[#050505]"
                  data-testid="contact-consent-checkbox"
                />
                <span className="text-sm text-[#A1A1AA] leading-relaxed">
                  {CONSENT_TEXT}{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00E5FF] hover:underline"
                    data-testid="contact-privacy-link"
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
              {errors.consent && (
                <div className="flex items-center gap-1 mt-2 text-xs text-red-400" role="alert">
                  <WarningCircle size={14} /> {errors.consent}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={loading}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                data-testid="contact-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary rounded-full px-6 flex items-center gap-2"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <>
                    <CircleNotch size={18} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Email my profile & connect
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="py-8 text-center" data-testid="contact-success">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00E5FF]/20 flex items-center justify-center">
              <Check size={32} className="text-[#00E5FF]" />
            </div>
            <p className="text-[#FAFAFA] text-base mb-2">Thank you, {firstName}.</p>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              Your profile is on its way to <strong>{formData.email}</strong>. Your details have
              also been shared with OnTenacity.
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 btn-primary rounded-full px-6"
              data-testid="contact-done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, name, value, onChange, placeholder, error, type = "text", testId, hint }) => (
  <div className="space-y-1">
    <Label htmlFor={name} className="text-[#FAFAFA] text-sm">
      {label}
    </Label>
    <Input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-input ${error ? "border-red-500/50" : ""}`}
      data-testid={testId}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
    />
    {hint && !error && (
      <p id={`${name}-hint`} className="text-xs text-[#71717A]">{hint}</p>
    )}
    {error && (
      <p id={`${name}-error`} className="text-xs text-red-400 flex items-center gap-1" role="alert">
        <WarningCircle size={12} /> {error}
      </p>
    )}
  </div>
);

export default QuizContactModal;
